import API from "../../../services/api";
import { fetchMonthlyAttendanceCalendar, fetchOtAllocationRegister } from "../../attendance/attendanceApi";
import { fetchWfHolidays } from "../../attendance/wfApi";
import { v2FetchPayrollCalendar } from "./SalaryApiV2";
import {
  buildHolidayDayMap,
  isWeekOffWeekday,
  monthRange,
  weekOffWeekdaysFromAttendance,
} from "../../attendance/attendanceCalendarUtils";

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function dateKey(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function clampDayInMonth(year, month, day) {
  const range = monthRange(`${year}-${String(month).padStart(2, "0")}`);
  const max = range?.daysInMonth || 28;
  const n = Math.max(1, Math.min(max, Number(day) || 1));
  return dateKey(year, month, n);
}

function resolveSalaryPaymentDate(year, month, salaryPaymentDay, payrollProcessingDay) {
  const payD = Number(salaryPaymentDay) || 10;
  const procD = Number(payrollProcessingDay) || 25;
  if (payD <= procD) {
    if (month === 12) return clampDayInMonth(year + 1, 1, payD);
    return clampDayInMonth(year, month + 1, payD);
  }
  return clampDayInMonth(year, month, payD);
}

function resolveDayStatus({ holidayName, weekdayIndex, weekOffWeekdays }) {
  if (holidayName) return "HOLIDAY";
  if (isWeekOffWeekday(weekdayIndex, weekOffWeekdays)) return "WEEKLY_OFF";
  return "WORKING_DAY";
}

function iterGridDates(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const startPad = first.getUTCDay() === 0 ? 6 : first.getUTCDay() - 1;
  const gridStart = new Date(first);
  gridStart.setUTCDate(gridStart.getUTCDate() - startPad);
  const last = new Date(Date.UTC(y, m, 0));
  const endPad = last.getUTCDay() === 0 ? 0 : 6 - (last.getUTCDay() === 0 ? 6 : last.getUTCDay() - 1);
  const gridEnd = new Date(last);
  gridEnd.setUTCDate(gridEnd.getUTCDate() + endPad);
  const out = [];
  const cur = new Date(gridStart);
  while (cur <= gridEnd) {
    const yy = cur.getUTCFullYear();
    const mm = cur.getUTCMonth() + 1;
    const dd = cur.getUTCDate();
    const dow = cur.getUTCDay();
    const weekdayIndex = dow === 0 ? 6 : dow - 1;
    out.push({
      iso: dateKey(yy, mm, dd),
      inMonth: yy === y && mm === m,
      weekdayIndex,
      day: dd,
      month: mm,
      year: yy,
    });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

function buildGridDays(monthKey, monthDays, holidayByIso, weekOffWeekdays, processingDate, paymentDate, todayKey) {
  const byDate = {};
  for (const d of monthDays) {
    byDate[d.date] = d;
  }
  const grid = [];
  for (const cell of iterGridDates(monthKey)) {
    if (byDate[cell.iso]) {
      grid.push(byDate[cell.iso]);
      continue;
    }
    const hol = holidayByIso[cell.iso];
    const status = resolveDayStatus({
      holidayName: hol?.name,
      weekdayIndex: cell.weekdayIndex,
      weekOffWeekdays,
    });
    grid.push({
      date: cell.iso,
      day_name: WEEKDAY_NAMES[cell.weekdayIndex] || "",
      in_month: cell.inMonth,
      is_today: cell.iso === todayKey,
      calendar_status: status,
      holiday_name: hol?.name || null,
      present: null,
      absent: null,
      leave: null,
      is_working_day: status === "WORKING_DAY",
      is_weekly_off: status === "WEEKLY_OFF",
      is_holiday: Boolean(hol),
      payroll_processing: cell.iso === processingDate,
      salary_payment: cell.iso === paymentDate,
      special_work_activity: false,
      shift: null,
    });
  }
  return grid;
}

async function buildFromLegacyApis({ year, month, location_id, department_id, employee_id }) {
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const range = monthRange(monthKey);
  const today = new Date();
  const todayKey = dateKey(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const [attCal, hrRes, holidays] = await Promise.all([
    fetchMonthlyAttendanceCalendar({ year, month, location_id, department_id, employee_id }),
    API.get("/organisation/me/hr-settings"),
    fetchWfHolidays({ from_date: range.from_date, to_date: range.to_date }),
  ]);

  const hr = hrRes.data?.hr_settings || {};
  const payrollSub = hrRes.data?.payroll_settings || hr.payroll || {};
  const att = hr.attendance || hrRes.data?.attendance_settings || {};
  const weekOffWeekdays = weekOffWeekdaysFromAttendance(att);
  const holidayMap = buildHolidayDayMap(holidays, monthKey);
  const holidayByIso = {};
  for (const h of holidays || []) {
    const m = String(h.holiday_date).match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) holidayByIso[m[1]] = { name: h.name || "Holiday" };
  }

  const summaryByDay = {};
  for (const s of attCal?.day_summary || []) {
    summaryByDay[s.day] = s;
  }

  const payrollProcessingDay = hr.payroll_processing_day ?? payrollSub.payroll_processing_day ?? 25;
  const salaryPaymentDay = hr.salary_payment_day ?? payrollSub.salary_payment_day ?? 10;
  const processingDate = clampDayInMonth(year, month, payrollProcessingDay);
  const paymentDate = resolveSalaryPaymentDate(year, month, salaryPaymentDay, payrollProcessingDay);

  let workingDays = 0;
  let weeklyOffs = 0;
  let holidaysCount = 0;
  let summaryPresent = 0;
  let summaryAbsent = 0;
  let summaryLeave = 0;
  let summaryHalf = 0;
  let summaryOt = 0;
  let summaryOtHours = 0;

  const monthDays = [];
  for (let day = 1; day <= range.daysInMonth; day += 1) {
    const iso = dateKey(year, month, day);
    const wd = new Date(Date.UTC(year, month - 1, day));
    const weekdayIndex = wd.getUTCDay() === 0 ? 6 : wd.getUTCDay() - 1;
    const hol = holidayMap[day];
    const status = resolveDayStatus({
      holidayName: hol?.name,
      weekdayIndex,
      weekOffWeekdays,
    });
    if (status === "HOLIDAY") holidaysCount += 1;
    else if (status === "WEEKLY_OFF") weeklyOffs += 1;
    else workingDays += 1;

    const sums = summaryByDay[day] || {};
    const present = sums.present || 0;
    const absent = sums.absent || 0;
    const leave = sums.leave || 0;
    const halfDay = sums.half_day || 0;
    const ot = sums.ot || 0;
    const otHours = Number(sums.ot_hours) || 0;
    const otPending = sums.ot_pending || 0;
    const otApproved = sums.ot_approved || 0;
    let otStatus = null;
    if (otPending && otApproved) otStatus = "MIXED";
    else if (otPending) otStatus = "PENDING";
    else if (otApproved) otStatus = "APPROVED";
    summaryPresent += present;
    summaryAbsent += absent;
    summaryLeave += leave;
    summaryHalf += halfDay;
    summaryOt += ot;
    summaryOtHours += otHours;

    const hasAtt = present || absent || leave || halfDay || ot;
    monthDays.push({
      date: iso,
      day_name: WEEKDAY_NAMES[weekdayIndex],
      in_month: true,
      is_today: iso === todayKey,
      calendar_status: status,
      holiday_name: hol?.name || null,
      present: present || absent || leave ? present : null,
      absent: present || absent || leave ? absent : null,
      leave: present || absent || leave ? leave : null,
      half_day: halfDay || null,
      ot: ot || null,
      ot_hours: ot ? otHours : null,
      ot_status: otStatus,
      is_working_day: status === "WORKING_DAY",
      is_weekly_off: status === "WEEKLY_OFF",
      is_holiday: Boolean(hol),
      payroll_processing: iso === processingDate,
      salary_payment: iso === paymentDate,
      special_work_activity: false,
      shift: null,
    });
  }

  const cycleStart = range.from_date;
  const cycleEnd = range.to_date;
  const formatCycle = (a, b) => {
    const fmt = (s) => {
      const p = s.split("-");
      const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };
    return `${fmt(a)} – ${fmt(b)}`;
  };

  const divisorRaw = hr.monthly_salary_divisor_days ?? payrollSub.monthly_salary_divisor_days;
  let workingDaysDivisor = null;
  if (divisorRaw != null && String(divisorRaw).trim() !== "") {
    const n = Number(divisorRaw);
    if (Number.isFinite(n)) workingDaysDivisor = n;
  }

  return {
    month: monthKey,
    year,
    month_number: month,
    today: todayKey,
    summary: {
      calendar_days: range.daysInMonth,
      working_days: workingDays,
      weekly_offs: weeklyOffs,
      holidays: holidaysCount,
      present: summaryPresent,
      absent: summaryAbsent,
      leaves: summaryLeave,
      half_days: summaryHalf,
      ot_allocations: summaryOt,
      ot_hours: Math.round(summaryOtHours * 100) / 100,
      employee_count: attCal?.employees?.length || 0,
    },
    payroll: {
      attendance_cycle_start: cycleStart,
      attendance_cycle_end: cycleEnd,
      attendance_cycle_label: formatCycle(cycleStart, cycleEnd),
      payroll_processing_date: processingDate,
      salary_payment_date: paymentDate,
      working_days_divisor: workingDaysDivisor,
      lop_enabled: payrollSub.lop_enabled !== false,
      attendance_proration_enabled: payrollSub.prorate_with_attendance !== false,
    },
    days: monthDays,
    grid_days: buildGridDays(
      monthKey,
      monthDays,
      holidayByIso,
      weekOffWeekdays,
      processingDate,
      paymentDate,
      todayKey
    ),
    records: monthDays,
    location_id: location_id || null,
    department_id: department_id || null,
    employee_id: employee_id || null,
    _source: "legacy",
  };
}

function isoDate(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(Number(day)).padStart(2, "0")}`;
}

function dayKey(value) {
  const m = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

async function fetchAllOtRows({ year, month, employee_id }) {
  const pageSize = 100;
  const first = await fetchOtAllocationRegister({ year, month, employee_id, page: 1, page_size: pageSize });
  const rows = [...(first?.rows || [])];
  const pages = Number(first?.total_pages) || 1;
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchOtAllocationRegister({ year, month, employee_id, page, page_size: pageSize });
    rows.push(...(next?.rows || []));
  }
  return rows;
}

function otStatusFromSet(statuses) {
  if (statuses.size === 1) return [...statuses][0];
  if (statuses.size > 1) return "MIXED";
  return null;
}

/**
 * Half day and OT come from the same live endpoints as Attendance Calendar and Overtime.
 * That keeps the payroll calendar aligned when the payroll-calendar payload is older.
 */
async function overlayLiveMarks(calendar, { year, month, location_id, department_id, employee_id, employee_ids }) {
  if (!calendar) return calendar;
  let attCal = null;
  let otRows = [];
  let haveHalf = false;
  let haveOt = false;
  try {
    const [attRes, otRes] = await Promise.allSettled([
      fetchMonthlyAttendanceCalendar({ year, month, location_id, department_id, employee_id }),
      fetchAllOtRows({ year, month, employee_id }),
    ]);
    if (attRes.status === "fulfilled") {
      attCal = attRes.value;
      haveHalf = true;
    }
    if (otRes.status === "fulfilled") {
      otRows = otRes.value || [];
      haveOt = true;
    }
  } catch {
    return calendar;
  }
  if (!haveHalf && !haveOt) return calendar;

  const allowed = Array.isArray(employee_ids)
    ? new Set(employee_ids.map(String))
    : null;
  const halfByDate = {};
  for (const emp of attCal?.employees || []) {
    if (allowed && !allowed.has(String(emp.employee_id))) continue;
    const days = emp.days || {};
    for (const [dayNum, code] of Object.entries(days)) {
      if (String(code || "").toUpperCase() !== "HD") continue;
      const key = isoDate(year, month, dayNum);
      halfByDate[key] = (halfByDate[key] || 0) + 1;
    }
  }

  const otByDate = {};
  for (const row of otRows) {
    if (allowed && !allowed.has(String(row.employee_id))) continue;
    const status = String(row.status || "").toUpperCase();
    if (status === "REJECTED" || status === "CANCELLED") continue;
    const key = dayKey(row.work_date);
    if (!key) continue;
    const bucket = otByDate[key] || { count: 0, hours: 0, statuses: new Set() };
    bucket.count += 1;
    bucket.hours += Number(row.ot_hours) || 0;
    if (status) bucket.statuses.add(status);
    otByDate[key] = bucket;
  }

  const presentAlreadyExcludesHalfDay = calendar.summary?.half_days != null;
  const seen = new Set();
  const apply = (day) => {
    if (!day || seen.has(day)) return;
    seen.add(day);
    const key = dayKey(day.date);
    if (!key || day.in_month === false) return;
    const half = haveHalf ? (halfByDate[key] || 0) : (day.half_day || 0);
    const ot = haveOt ? otByDate[key] : null;
    if (haveHalf && !presentAlreadyExcludesHalfDay && half && day.present != null) {
      day.present = Math.max(0, Number(day.present) - half);
    }
    if (haveHalf) day.half_day = half || null;
    if (haveOt) {
      if (ot?.count) {
        day.ot = ot.count;
        day.ot_hours = Math.round(ot.hours * 100) / 100;
        day.ot_status = otStatusFromSet(ot.statuses);
      } else {
        day.ot = null;
        day.ot_hours = null;
        day.ot_status = null;
      }
    }
  };
  for (const list of [calendar.days, calendar.grid_days, calendar.records]) {
    (list || []).forEach(apply);
  }

  let halfDays = 0;
  let otAllocations = 0;
  let otHours = 0;
  for (const day of calendar.days || []) {
    halfDays += day.half_day || 0;
    otAllocations += day.ot || 0;
    otHours += Number(day.ot_hours) || 0;
  }
  calendar.summary = {
    ...(calendar.summary || {}),
    half_days: halfDays,
    ot_allocations: otAllocations,
    ot_hours: Math.round(otHours * 100) / 100,
  };
  return calendar;
}

/** Primary: salary v2 read model. Fallback: attendance month + HR settings + holidays. */
export async function loadPayrollCalendar({
  year,
  month,
  location_id,
  department_id,
  employee_id,
  employee_ids,
} = {}) {
  const scope = { year, month, location_id, department_id, employee_id, employee_ids };
  try {
    const data = await v2FetchPayrollCalendar({ year, month, location_id, department_id, employee_id });
    return overlayLiveMarks({ ...data, _source: "v2" }, scope);
  } catch (primaryErr) {
    try {
      const legacy = await buildFromLegacyApis({ year, month, location_id, department_id, employee_id });
      return overlayLiveMarks(legacy, scope);
    } catch (fallbackErr) {
      const msg = primaryErr?.message || "Failed to load payroll calendar";
      const err = new Error(msg);
      err.cause = fallbackErr;
      throw err;
    }
  }
}
